import React, { useLayoutEffect, useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkHtml from 'remark-html';
import { ActionIcon, Box, Text, Tooltip, createStyles } from '@mantine/core';
import { IconCircleChevronDown } from '@tabler/icons';
import { useSearchParams } from 'react-router-dom';
import { useMessageFeedback, useSessionOnePagers, useCreateOnePager } from '../../../hooks/useDiscoveryAgent';
import { useAuth0 } from '@auth0/auth0-react';
import type { Artifact, DiscoverySessionMessage } from '../../../api/discoveryAgent';
import { sendDiscoveryMessageWithRecovery } from '../../../api/discoveryAgent';
import { buildCreateIdeaHiddenMessage } from '../../../utils/agent/idea';
import { useDiscoveryStore } from '../../../stores/useDiscoveryStore';
import { decodeHtmlEntities } from '../../../utils/decodeHtmlEntities';
import { stripHtml, truncateText } from '../../../utils';
import ExploreEvidencesCard from "./ExploreEvidencesCard";
import ExploreIdeasListCardSection from './ExploreIdeasListCardSection';
import { openIdeaLinksInNewTab } from '../utils/openIdeaLinksInNewTab';
import {
  buildExploreMessageSegments,
  buildIdeasListStateKey,
  normalizeIdeasListMarkerParagraphs,
  type ExploreMessageSegment,
} from './explore-message-segments';
import {
  applyIdeaIdMappingToMessageContent,
  isValidMongoObjectId,
  resolveExploreMessageIdeasListIds,
} from '../utils/resolve-explore-idea-ids';
import MessageFeedback from './MessageFeedback';
import { CreateIdeaFromChatModal } from '../../../pages/ai-agent/createIdea/CreateIdeaFromChatModal';
import { getPersistedMessageId, isHiddenCreateIdeaUserMessage, parseIdeaCreatedMessage } from '../../../pages/ai-agent/createIdea/createIdeaFromChatPrompt';

const ASSISTANT_BUBBLE_PADDING_X = 16;

/** Vertical space between the evidence summary card and follow-up markdown (model suggestions after the card). */
const EXPLORE_POST_ARTIFACT_GAP_PX = 40;

/** Left offset for list items in the trailing follow-up block only (e.g. after the last evidence card). */
const EXPLORE_FOLLOW_UP_LIST_ITEM_MARGIN_LEFT_PX = 40;

const useStyles = createStyles(() => ({
  thinkingText: {
    backgroundImage: 'linear-gradient(to right, #333, #888, #5C5CEB, #888, #333)',
    backgroundSize: '400% 100%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'exploreThinkingAnimation 4s linear infinite',
    '@keyframes exploreThinkingAnimation': {
      '0%': { backgroundPosition: '0% 50%' },
      '100%': { backgroundPosition: '-800% 50%' },
    },
  },
}));

interface ExploreChatMessageListProps {
  messages: DiscoverySessionMessage[];
  sessionId?: string;
  isLoading?: boolean;
  isSending?: boolean;
  chatStatusError?: string | null;
  handleLeftPanelCollapse?: () => void;
  scrollToPromptArea?: () => void;
  artifactIdeaIdByAlias?: Map<string, string>;
  onIdeaCreated?: () => void;
}

/** Animated text shown on the assistant side while waiting for response */
const AnimatedThinkingText: React.FC = () => {
  const { classes } = useStyles();
  return (
    <Box sx={{ alignSelf: 'flex-start', padding: '12px 16px' }}>
      <Text size="sm" className={classes.thinkingText} sx={{ fontSize: 15 }}>
        Analyzing your request...
      </Text>
    </Box>
  );
};

const ExploreChatMessageList: React.FC<ExploreChatMessageListProps> = ({
  messages,
  sessionId = '',
  isLoading,
  isSending = false,
  chatStatusError,
  handleLeftPanelCollapse,
  artifactIdeaIdByAlias,
  scrollToPromptArea,
  onIdeaCreated,
}) => {
  const messageFeedbackMutation = useMessageFeedback(sessionId);
  const { data: sessionOnePagers = [] } = useSessionOnePagers(sessionId);

  const sortedMessages = useMemo(() => {
    const sorted = [...messages].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    // Hide CREATE_IDEA hidden user messages and the agent reply each one triggered.
    //
    // The create-idea prompt is sent in the background while the composer stays
    // enabled, so an unrelated user message can land between the hidden prompt and
    // its agent reply (the reply is no longer guaranteed to be at index i + 1).
    // Find each hidden prompt's reply as the first *agent* message after it,
    // skipping interleaved user prompts and idea_created notifications. Each agent
    // reply is claimed at most once so consecutive create-idea prompts pair with
    // distinct replies (in request order).
    //
    // A create-idea reply carrying a `suggested_idea` artifact is already removed
    // by the filter below; when we reach one we still claim it (so we don't hide a
    // later, unrelated answer) but skip adding it to the hidden set.
    const hiddenAgentIndices = new Set<number>();
    const claimedAgentIndices = new Set<number>();
    sorted.forEach((m, i) => {
      const isHiddenMsg = m.hidden === true || (m.hidden === undefined && isHiddenCreateIdeaUserMessage(m.content));
      if (m.messageType === 'user' && isHiddenMsg) {
        for (let j = i + 1; j < sorted.length; j++) {
          const candidate = sorted[j];
          if (candidate.messageType !== 'agent') continue; // skip interleaved user / idea_created
          if (claimedAgentIndices.has(j)) continue; // already paired with an earlier prompt
          claimedAgentIndices.add(j);
          const isSuggestedIdeaReply = candidate.metadata?.artifacts?.[0]?.type === 'suggested_idea';
          if (!isSuggestedIdeaReply) hiddenAgentIndices.add(j);
          break;
        }
      }
      // idea_created is a local notification — no agent reply to suppress
    });
    return sorted.filter((m, i) => {
      // If the backend explicitly sets hidden=false, always show (no-artifact reveal case)
      if (m.hidden === false) return true;
      if (m.messageType === 'user' && (m.hidden === true || (m.hidden === undefined && isHiddenCreateIdeaUserMessage(m.content)))) return false;
      if (m.messageType === 'idea_created') return true; // always show
      // Hide agent responses that triggered the create-idea modal (suggested_idea artifact)
      if (m.messageType === 'agent' && m.metadata?.artifacts?.[0]?.type === 'suggested_idea') return false;
      if (hiddenAgentIndices.has(i)) return false;
      return true;
    });
  }, [messages]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 0',
      }}
    >
      {isLoading && sortedMessages.length === 0 ? (
        <Text size="sm" c="dimmed">
          Loading...
        </Text>
      ) : (
        <>
          {sortedMessages.map((msg, index) => {
            // For agent messages, find the closest preceding user message — its content is
            // the queryText and its _id is the key the one-pager is stored under.
            const precedingUserMessage = msg.messageType === 'agent'
              ? (() => {
                for (let i = index - 1; i >= 0; i--) {
                  if (sortedMessages[i].messageType === 'user') return sortedMessages[i];
                }
                return undefined;
              })()
              : undefined;
            const queryText = precedingUserMessage?.content;
            const queryMessageId = precedingUserMessage
              ? getPersistedMessageId(precedingUserMessage)
              : undefined;
            return (
              <ExploreChatMessageBubble
                key={msg.id ? String(msg.id) : `${msg.timestamp}-${index}`}
                message={msg}
                sessionId={sessionId}
                messageFeedbackMutate={messageFeedbackMutation.mutate}
                messageFeedbackPending={messageFeedbackMutation.isPending}
                messageFeedbackVariables={messageFeedbackMutation.variables}
                handleLeftPanelCollapse={handleLeftPanelCollapse}
                artifactIdeaIdByAlias={artifactIdeaIdByAlias}
                scrollToPromptArea={scrollToPromptArea}
                queryText={queryText}
                queryMessageId={queryMessageId}
                sessionOnePagers={sessionOnePagers}
                onIdeaCreated={onIdeaCreated}
              />
            );
          })}
          {isSending && !chatStatusError && sortedMessages.length > 0 && <AnimatedThinkingText />}
          {chatStatusError && sortedMessages.length > 0 && (
            <Box sx={{ alignSelf: 'flex-start', padding: '12px 16px' }}>
              <Text size="sm" color="red" sx={{ fontSize: 15 }}>
                Analysis failed. Please try sending your message again.
              </Text>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

interface ExploreChatMessageBubbleProps {
  message: DiscoverySessionMessage;
  sessionId: string;
  sessionOnePagers?: import('../../../api/discoveryAgent').OnePager[];
  messageFeedbackMutate: ReturnType<typeof useMessageFeedback>['mutate'];
  messageFeedbackPending: boolean;
  messageFeedbackVariables: ReturnType<typeof useMessageFeedback>['variables'];
  handleLeftPanelCollapse?: () => void;
  artifactIdeaIdByAlias?: Map<string, string>;
  scrollToPromptArea?: () => void;
  queryText?: string;
  queryMessageId?: string;
  onIdeaCreated?: () => void;
}

const ExploreChatMessageBubble: React.FC<ExploreChatMessageBubbleProps> = ({
  message,
  sessionId,
  messageFeedbackMutate,
  messageFeedbackPending,
  messageFeedbackVariables,
  handleLeftPanelCollapse,
  artifactIdeaIdByAlias,
  scrollToPromptArea,
  queryText,
  queryMessageId,
  sessionOnePagers = [],
  onIdeaCreated,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isUser = message.messageType === 'user';
  const content = message.content;

  const [, setSearchParams] = useSearchParams();
  const setActiveEvidenceIds = useDiscoveryStore((s) => s.setActiveEvidenceIds);
  const setActiveEvidenceLinkText = useDiscoveryStore((s) => s.setActiveEvidenceLinkText);
  const setActiveInsightSummary = useDiscoveryStore((s) => s.setActiveInsightSummary);
  const setActiveMessageTimestamp = useDiscoveryStore((s) => s.setActiveMessageTimestamp);
  const setInsightsPanelOpen = useDiscoveryStore((s) => s.setInsightsPanelOpen);
  const setArtifactEvidenceIds = useDiscoveryStore((s) => s.setArtifactEvidenceIds);
  // setPendingCreateIdeaModal is kept for legacy compatibility but no longer called here
  const setPendingArtifactCreateIdeaTitle = useDiscoveryStore((s) => s.setPendingArtifactCreateIdeaTitle);
  const setPendingArtifactCreateIdeaArtifactId = useDiscoveryStore((s) => s.setPendingArtifactCreateIdeaArtifactId);
  const setActiveArtifactId = useDiscoveryStore((s) => s.setActiveArtifactId);
  const setActiveArtifactRecordId = useDiscoveryStore((s) => s.setActiveArtifactRecordId);
  const setLastResolvedDeepLinkArtifactId = useDiscoveryStore((s) => s.setLastResolvedDeepLinkArtifactId);
  const activeEvidenceIds = useDiscoveryStore((s) => s.activeEvidenceIds);
  const setInputValue = useDiscoveryStore((s) => s.setInputValue);
  const setDiscoveryPromptCategory = useDiscoveryStore((s) => s.setDiscoveryPromptCategory);

  const markdownProcessor = useMemo(() => {
    return unified()
      .use(remarkParse)
      .use(remarkGfm, { singleTilde: false })
      .use(remarkHtml, { sanitize: false });
  }, []);

  const handleEvidenceLearnMore = (
    ids: string[],
    text: string,
    _artifactAlias?: string,
    summary?: string,
    artifactId?: string
  ) => {
    if (ids.length > 0) {
      setArtifactEvidenceIds(ids);
      setActiveEvidenceIds(ids);
      setActiveEvidenceLinkText(text);
      setActiveInsightSummary(summary ?? '');
      setActiveMessageTimestamp(new Date(message.timestamp));
      setInsightsPanelOpen(true);
      handleLeftPanelCollapse?.();
      if (artifactId) {
        // Card / artifact wild-link click. Writing the guards BEFORE the
        // URL mutation short-circuits the deep-link effect (which would
        // otherwise re-fetch the artifact we already have) while still
        // updating the URL so the view is shareable / restorable.
        setActiveArtifactId(artifactId);
        setActiveArtifactRecordId(artifactId);
        setLastResolvedDeepLinkArtifactId(artifactId);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('ideaId');
            next.set('artifactId', artifactId);
            return next;
          },
          { replace: false }
        );
      } else {
        // Non-artifact evidence link: make sure no stale artifactId sticks
        // around in the URL (otherwise a refresh would re-open the wrong
        // artifact view on top of the evidence).
        setActiveArtifactId(null);
        setActiveArtifactRecordId(null);
        setSearchParams(
          (prev) => {
            if (!prev.has('artifactId') && !prev.has('ideaId')) return prev;
            const next = new URLSearchParams(prev);
            next.delete('artifactId');
            next.delete('ideaId');
            return next;
          },
          { replace: true }
        );
      }
    }
  };

  const handleIdeaRowClick = (
    ideaId: string,
    ideasListArtifactId?: string,
    ideasPage?: number
  ) => {
    if (!ideaId || !isValidMongoObjectId(ideaId)) return;
    setArtifactEvidenceIds([]);
    setActiveEvidenceIds([]);
    setActiveEvidenceLinkText('');
    setActiveInsightSummary('');
    const artifactRecordId = ideasListArtifactId?.trim() || null;
    setActiveArtifactId(artifactRecordId);
    setActiveArtifactRecordId(artifactRecordId);
    setActiveMessageTimestamp(new Date(message.timestamp));
    setInsightsPanelOpen(true);
    handleLeftPanelCollapse?.();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('selectedArtifact');
        if (artifactRecordId) {
          next.set('artifactId', artifactRecordId);
        } else {
          next.delete('artifactId');
        }
        next.set('ideaId', ideaId);
        if (ideasPage && ideasPage > 1) {
          next.set('ideasPage', String(ideasPage));
        } else {
          next.delete('ideasPage');
        }
        return next;
      },
      { replace: false }
    );
  };

  const handleArtifactDrillDownToPrompt = (rawTitle: string) => {
    const t = decodeHtmlEntities(rawTitle).replace(/\s+/g, ' ').trim();
    scrollToPromptArea?.();
    setInputValue(`Drill down into this topic: ${t}`);
    setDiscoveryPromptCategory(null, null);
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLTextAreaElement>('#explore-discovery-composer textarea')
        ?.focus();
    });
  };

  const handleSpanLinkClick = (
    ids: string[],
    title: string,
    summary: string
  ) => {
    if (ids.length > 0) {
      setActiveEvidenceIds(ids);
      setActiveEvidenceLinkText(title);
      setActiveInsightSummary(summary);
      setActiveMessageTimestamp(new Date(message.timestamp));
      setInsightsPanelOpen(true);
      handleLeftPanelCollapse?.();
      // Evidence (non-artifact) view — make sure no stale artifactId
      // deep-link remains in the URL.
      setActiveArtifactId(null);
      setActiveArtifactRecordId(null);
      setSearchParams(
        (prev) => {
          if (!prev.has('artifactId') && !prev.has('ideaId')) return prev;
          const next = new URLSearchParams(prev);
          next.delete('artifactId');
          next.delete('ideaId');
          return next;
        },
        { replace: true }
      );
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const extractContextFromParent = (evidenceLink: Element): { title: string; summary: string } => {
      // Include headings: spans are often placed inside h1–h6 (e.g. ### Section - <span>)
      const parent = evidenceLink.closest('li, p, h1, h2, h3, h4, h5, h6');
      if (!parent) return { title: '', summary: '' };

      let title = '';
      let summary = '';

      const isHeading = /^h[1-6]$/i.test(parent.tagName);

      if (isHeading) {
        // Extract text that precedes the span inside the heading
        const parentHtml = parent.innerHTML;
        const spanOuterHtml = (evidenceLink as HTMLElement).outerHTML;
        const beforeSpanHtml = parentHtml.slice(0, parentHtml.indexOf(spanOuterHtml));
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = beforeSpanHtml;
        // Strip trailing separator (e.g. " - " or ": ")
        title = (tempDiv.textContent ?? '').trim().replace(/[\s\-–—:]+$/, '').trim();

        // Summary lives in the next sibling <p> when the span is in a heading
        const nextSibling = parent.nextElementSibling;
        if (nextSibling && nextSibling.tagName.toLowerCase() === 'p') {
          summary = nextSibling.textContent?.trim() ?? '';
        }
      } else {
        const strongEl = parent.querySelector('strong');
        if (strongEl) {
          title = strongEl.textContent?.trim() ?? '';
        }

        const parentHtml = parent.innerHTML;
        const spanOuterHtml = (evidenceLink as HTMLElement).outerHTML;
        const afterSpanIndex = parentHtml.indexOf(spanOuterHtml) + spanOuterHtml.length;
        const afterSpanHtml = parentHtml.slice(afterSpanIndex);

        const colonMatch = afterSpanHtml.match(/^\s*(:)?\s*/);
        const hasColonAfterSpan = !!colonMatch?.[1];
        const textAfterColon = colonMatch
          ? afterSpanHtml.slice(colonMatch[0].length)
          : afterSpanHtml;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = textAfterColon;
        summary = tempDiv.textContent?.trim() ?? '';

        // Fallback: if the description was split into a separate sibling <p>
        // (e.g. an HTML comment interrupted the paragraph), grab it from there.
        if (!summary && hasColonAfterSpan) {
          const nextSibling = parent.nextElementSibling;
          if (nextSibling && nextSibling.tagName.toLowerCase() === 'p') {
            summary = nextSibling.textContent?.trim() ?? '';
          }
        }
      }

      return { title, summary };
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!container.contains(target)) return;

      const evidenceLink = target.closest('.evidence-link');
      if (!evidenceLink) return;
      e.preventDefault();

      const artifactIdAttr = evidenceLink.getAttribute('data-artifact-id');
      if (artifactIdAttr) {
        const artifacts = message.metadata?.artifacts ?? [];
        const artifact = artifacts.find((a) => a._id === artifactIdAttr);
        if (artifact) {
          // Route through handleEvidenceLearnMore so URL + deep-link guards
          // are updated atomically (same code path as the card click).
          handleEvidenceLearnMore(
            artifact.evidence_ids,
            artifact.title,
            artifact.alias,
            artifact.description,
            artifactIdAttr
          );
          return;
        }
      }

      const idsStr = evidenceLink.getAttribute('data-evidence-ids');
      if (idsStr) {
        const ids = idsStr.split(',').map((id) => id.trim());
        const { title, summary } = extractContextFromParent(evidenceLink);
        handleSpanLinkClick(ids, title || 'Insight', summary);
      }
    };

    const handleMouseEnter = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const evidenceLink = target.closest('.evidence-link') as HTMLElement;
      if (evidenceLink) {
        evidenceLink.style.backgroundColor = 'rgba(92, 92, 235, 0.12)';
      }
    };

    const handleMouseLeave = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const evidenceLink = target.closest('.evidence-link') as HTMLElement;
      if (evidenceLink) {
        evidenceLink.style.backgroundColor = 'transparent';
      }
    };

    container.addEventListener('click', handleClick);
    container.addEventListener('mouseover', handleMouseEnter);
    container.addEventListener('mouseout', handleMouseLeave);

    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('mouseover', handleMouseEnter);
      container.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [content]);
  
  const handleCreateIdeaFromArtifact = (
    ids: string[],
    text: string,
    artifactAlias?: string,
    summary?: string,
    artifactRecordId?: string
  ) => {
    if (ids.length === 0) return;
    // Only signal the modal — do NOT touch activeEvidenceIds or any insights-panel
    // state, as that would mark the card as "Expanded" and open the insights panel.
    setPendingArtifactCreateIdeaArtifactId(artifactRecordId?.trim() || null);
    setPendingArtifactCreateIdeaTitle(text);
  };

  const itemSegments = useMemo((): ExploreMessageSegment[] => {
    const artifacts = message.metadata?.artifacts;
    const hasArtifacts = Boolean(artifacts && artifacts.length > 0);
    const mappedContent = applyIdeaIdMappingToMessageContent(content, message);
    const resolvedIdeasListIds = resolveExploreMessageIdeasListIds(message);
    const markdownForHtml = prepareAssistantMarkdownForArtifacts(mappedContent, hasArtifacts);
    const rawHtmlContent = markdownProcessor.processSync(markdownForHtml).toString();
    let htmlContent = openIdeaLinksInNewTab(processEvidenceLinks(rawHtmlContent));
    htmlContent = normalizeIdeasListMarkerParagraphs(htmlContent);
    if (hasArtifacts) {
      htmlContent = normalizeArtifactEndMarkerParagraphs(htmlContent);
    }

    const split = buildExploreMessageSegments(htmlContent, artifacts);
    return split.map((segment, index, segments) => {
      if (segment.type === 'ideas-list' && resolvedIdeasListIds.length > 0) {
        return { ...segment, ideaIds: resolvedIdeasListIds };
      }
      if (segment.type !== 'html') return segment;
      const postArtifact = index > 0 && segments[index - 1]?.type === 'artifact-card';
      if (!postArtifact) return segment;
      return { ...segment, value: wrapExplorePostArtifactHtml(segment.value) };
    });
  }, [content, markdownProcessor, message.metadata?.artifacts, message.metadata?.ideaIdsMap, message.metadata?.ideasListIds]);

  const persistedAgentMessageId = getPersistedMessageId(message);
  const onePager =
    sessionOnePagers.find(
      (p) => !p.artifactId && (p.messageId === persistedAgentMessageId || p.messageId === queryMessageId)
    ) ?? null;
  // POST: generates via ML + saves to DB in one call; seeds session cache on success
  const createOnePagerMutation = useCreateOnePager(sessionId);
  const [onePagerModalOpen, setOnePagerModalOpen] = useState(false);
  // Artifact-level one-pager state (separate from message-level)
  const artifactOnePagerMutation = useCreateOnePager(sessionId);
  const [onePagerArtifactId, setOnePagerArtifactId] = useState<string | null>(null);
  const auth0 = useAuth0();

  const activeOnePagerArtifact = useMemo(
    () =>
      onePagerArtifactId
        ? (message.metadata?.artifacts?.find((a) => a._id === onePagerArtifactId) ?? null)
        : null,
    [onePagerArtifactId, message.metadata?.artifacts]
  );

  const artifactOnePagerFetchContent = useCallback(async (): Promise<{
    title: string;
    description: string;
    alreadyPersisted?: boolean;
  }> => {
    if (!activeOnePagerArtifact || !sessionId || !persistedAgentMessageId) {
      return { title: '', description: '' };
    }

    // Already saved — return immediately without a chat round-trip
    const existing = sessionOnePagers.find((p) => p.artifactId === activeOnePagerArtifact._id);
    if (existing) {
      return { title: existing.title, description: existing.description, alreadyPersisted: true };
    }

    // Call /chat to generate title + description
    const hiddenMsg = buildCreateIdeaHiddenMessage(activeOnePagerArtifact.title);
    const result = await sendDiscoveryMessageWithRecovery(
      sessionId,
      hiddenMsg,
      auth0,
      undefined,
      { hidden: true, recoveryBudgetMs: 90_000 }
    );

    if (!result.success || result.status === 'pending') {
      throw new Error(result.message || 'Failed to generate one-pager content');
    }

    const chatArtifact = result.artifacts?.[0];
    const title = chatArtifact?.title ?? '';
    const description = chatArtifact?.description ?? '';

    // Save to /one-pager with artifactId — mutation updates the session cache
    await artifactOnePagerMutation.mutateAsync({
      sessionId,
      messageId: persistedAgentMessageId,
      artifactId: activeOnePagerArtifact._id,
      queryText: activeOnePagerArtifact.title,
      title,
      description,
    });

    return { title, description, alreadyPersisted: true };
  }, [
    activeOnePagerArtifact,
    sessionOnePagers,
    sessionId,
    persistedAgentMessageId,
    auth0,
    artifactOnePagerMutation,
  ]);

  // Message-level one-pager: generate ML content via hidden /chat, then save. Mirrors the artifact flow.
  const messageLevelFetchContent = useCallback(async (): Promise<{
    title: string;
    description: string;
    alreadyPersisted?: boolean;
  }> => {
    if (!sessionId || !persistedAgentMessageId) {
      return { title: '', description: '' };
    }

    const existing = sessionOnePagers.find(
      (p) => !p.artifactId && (p.messageId === persistedAgentMessageId || p.messageId === queryMessageId)
    );
    if (existing) {
      return { title: existing.title, description: existing.description, alreadyPersisted: true };
    }

    // Call /chat to generate title + description
    const hiddenMsg = buildCreateIdeaHiddenMessage(queryText ?? '');
    const result = await sendDiscoveryMessageWithRecovery(
      sessionId,
      hiddenMsg,
      auth0,
      undefined,
      { hidden: true, recoveryBudgetMs: 90_000 }
    );

    if (!result.success || result.status === 'pending') {
      throw new Error(result.message || 'Failed to generate one-pager content');
    }

    const chatArtifact = result.artifacts?.[0];
    const title = chatArtifact?.title ?? '';
    const description = chatArtifact?.description ?? '';

    // Save to /one-pager with generated content — mutation updates the session cache
    await createOnePagerMutation.mutateAsync({
      sessionId,
      messageId: persistedAgentMessageId,
      queryText: queryText ?? '',
      title,
      description,
    });

    return { title, description, alreadyPersisted: true };
  }, [
    sessionId,
    persistedAgentMessageId,
    sessionOnePagers,
    queryText,
    queryMessageId,
    auth0,
    createOnePagerMutation,
  ]);

  // Don't render agent messages whose content was cleared (e.g. suggested_idea responses)
  if (message.messageType === 'agent' && !content?.trim()) return null;

  if (message.messageType === 'idea_created') {
    const { ideaId, title } = parseIdeaCreatedMessage(content ?? '');
    return (
      <Box
        sx={{
          alignSelf: 'flex-end',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          borderRadius: 20,
          backgroundColor: 'rgba(18, 184, 134, 0.1)',
          border: '1px solid rgba(18, 184, 134, 0.3)',
          marginTop: 16,
          marginBottom: 16,
          maxWidth: '85%',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 18,
            height: 18,
            borderRadius: '50%',
            backgroundColor: '#12B886',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Box>
        <Text size="sm" color="#0E7B58" sx={{ lineHeight: 1.4, minWidth: 0 }}>
          Idea created:{' '}
          {ideaId ? (
            <Tooltip
              label={title}
              withArrow
              multiline
              w={300}
              disabled={!title || title.length <= 60}
            >
              <Text
                component="a"
                href={`/idea/${ideaId}`}
                target="_blank"
                rel="noopener noreferrer"
                fw={600}
                sx={{ color: '#0E7B58', textDecoration: 'underline', '&:hover': { color: '#0A5E43' } }}
              >
                {title.length > 60 ? truncateText(title, 60) : title || 'View idea'}
              </Text>
            </Tooltip>
          ) : (
            <Text component="span" fw={600}>{title || 'View idea'}</Text>
          )}
        </Text>
      </Box>
    );
  }

  if (isUser) {
    return (
      <Box
        sx={{
          alignSelf: 'flex-end',
          maxWidth: '85%',
          minWidth: 0,
          padding: '12px 16px',
          borderRadius: 20,
          backgroundColor: 'rgba(92, 92, 235, 0.12)',
          marginTop: 44,
          marginBottom: 20,
          color: '#131416',
        }}
      >
        <Text
          size="sm"
          sx={{
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {content}
        </Text>
      </Box>
    );
  }


  const persistedMessageId = persistedAgentMessageId;

  const handleCreateOnePager = () => {
    if (!persistedMessageId || !queryMessageId) return;
    // fetchContent handles both create (ML generation + save) and view (return existing) paths
    setOnePagerModalOpen(true);
  };

  return (
    <Box
      sx={{
        alignSelf: 'flex-start',
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          alignSelf: 'flex-start',
          width: '100%',
          minWidth: 0,
          padding: `0 ${ASSISTANT_BUBBLE_PADDING_X}px`,
          color: '#212529',
          overflowWrap: 'anywhere',
          '& p': { marginBottom: '0.5rem', lineHeight: '1.5' },
          '& h1': { fontSize: '1.4em', fontWeight: 600, margin: '0.75rem 0 0.5rem 0' },
          '& h2': { fontSize: '1.3em', fontWeight: 600, margin: '0.75rem 0 0.5rem 0' },
          '& h3': { fontSize: '1.2em', fontWeight: 600, margin: '0.75rem 0 0.5rem 0' },
          '& h4, & h5, & h6': { fontWeight: 600, margin: '0.75rem 0 0.5rem 0' },
          '& ul, & ol': { margin: '0.5rem 0', paddingLeft: '1.5rem' },
          '& li': { marginBottom: '0.25rem' },
          '& pre': {
            backgroundColor: isUser ? 'rgba(255,255,255,0.1)' : '#f5f5f5',
            padding: '8px 12px',
            borderRadius: '4px',
            overflowX: 'auto',
            fontSize: '13px',
            margin: '0.5rem 0',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
          },
          '& code': {
            backgroundColor: isUser ? 'rgba(255,255,255,0.15)' : '#f0f0f0',
            padding: '2px 4px',
            borderRadius: '3px',
            fontSize: '13px',
            fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
          },
          '& blockquote': {
            borderLeft: '3px solid',
            borderLeftColor: isUser ? 'rgba(255,255,255,0.3)' : '#ddd',
            paddingLeft: '1rem',
            margin: '0.5rem 0',
            fontStyle: 'italic',
          },
          '& a': { color: isUser ? '#B3D9FF' : '#4A90E2', textDecoration: 'underline' },
          '& strong': { fontWeight: 600 },
          '& em': { fontStyle: 'italic' },
          '& .evidence-link': { display: 'inline-flex !important' },
        }}
      >
        <Box
          component="div"
          sx={{
            fontSize: 16,
            lineHeight: 1.6,
            minWidth: 0,
            overflowWrap: 'anywhere',
            '& *': { maxWidth: '100%' },
          }}
        >
          {itemSegments.map((segment, index) => {
            if (segment.type === 'html') {
              const next = itemSegments[index + 1];
              const followingArtifact =
              next?.type === 'artifact-card' ? next.artifact : undefined;
              const isPostArtifactBlock =
                index > 0 && itemSegments[index - 1]?.type === 'artifact-card';
              /** Suggestions after the final card — not topic lines before another card. */
              const isTrailingFollowUpBlock =
                isPostArtifactBlock && !followingArtifact;
              if (followingArtifact) {
                return (
                  <HtmlSegmentWithArtifactDrill
                    key={`item-${index}`}
                    html={segment.value}
                    artifactTitle={followingArtifact.title}
                    onDrillDown={handleArtifactDrillDownToPrompt}
                  />
                );
              }
              return (
                <Box
                  key={`item-${index}`}
                  component="div"
                  dangerouslySetInnerHTML={{ __html: segment.value }}
                  sx={{
                    ...(isTrailingFollowUpBlock
                      ? {
                        '& li': {
                          marginLeft: `${EXPLORE_FOLLOW_UP_LIST_ITEM_MARGIN_LEFT_PX}px`,
                          marginBottom: '0.25rem',
                        },
                      }
                      : {}),
                    '& p:last-child': { marginBottom: 0 },
                  }}
                />
              );
            }

            if (segment.type === 'ideas-list') {
              const messageId = getPersistedMessageId(message) ?? message._id ?? message.id ?? 'unknown';
              const stateKey = buildIdeasListStateKey(messageId, index, segment.artifactId);
              return (
                <ExploreIdeasListCardSection
                  key={`ideas-${stateKey}`}
                  ideaIds={segment.ideaIds}
                  artifactId={segment.artifactId}
                  stateKey={stateKey}
                  onRowClick={handleIdeaRowClick}
                />
              );
            }

            const { artifact } = segment;
            const isExpanded =
            artifact.evidence_ids.length > 0 &&
            artifact.evidence_ids.length === activeEvidenceIds.length &&
            artifact.evidence_ids.every((id) => activeEvidenceIds.includes(id));
            const linkedIdeaId =
            (artifact._id && artifactIdeaIdByAlias?.get(artifact._id)) ??
            artifactIdeaIdByAlias?.get(artifact.alias) ??
            null;
            const hasFollowingHtml = itemSegments[index + 1]?.type === 'html';
            return (
              <Box
                key={`card-${index}`}
                mt="xs"
                sx={{
                  marginBottom: hasFollowingHtml ? 0 : `${EXPLORE_POST_ARTIFACT_GAP_PX}px`,
                }}
                data-artifact-id={artifact._id}
              >
                <ExploreEvidencesCard
                  title={artifact.title}
                  evidenceIds={artifact.evidence_ids}
                  evidenceNumber={artifact.evidence_ids.length}
                  isExpanded={isExpanded}
                  linkedIdeaId={linkedIdeaId}
                  onCollapseLeftPanel={handleLeftPanelCollapse}
                  onLearnMore={() =>
                    handleEvidenceLearnMore(
                      artifact.evidence_ids,
                      artifact.title,
                      artifact.alias,
                      artifact.description,
                      artifact._id
                    )
                  }
                  onCreateIdea={() =>
                    handleCreateIdeaFromArtifact(
                      artifact.evidence_ids,
                      artifact.title,
                      artifact.alias,
                      artifact.description,
                      artifact._id
                    )
                  }
                  existingOnePager={
                    !linkedIdeaId && artifact._id
                      ? (sessionOnePagers.find((p) => p.artifactId === artifact._id) ?? null)
                      : null
                  }
                  onCreateOnePager={
                    !linkedIdeaId && artifact._id
                      ? () => setOnePagerArtifactId(artifact._id ?? null)
                      : undefined
                  }
                  onViewOnePager={
                    !linkedIdeaId && artifact._id
                      ? () => setOnePagerArtifactId(artifact._id ?? null)
                      : undefined
                  }
                />
              </Box>
            );
          })}
        </Box>
      </Box>
      {sessionId ? (
        <>
          <MessageFeedback
            messageId={persistedMessageId ?? ''}
            currentFeedback={message.feedback}
            mutate={messageFeedbackMutate}
            isMutatingThisMessage={
              Boolean(persistedMessageId) &&
              messageFeedbackPending &&
              messageFeedbackVariables?.messageId === persistedMessageId
            }
            hasOnePager={onePager !== null && onePager !== undefined}
            isCreatingOnePager={createOnePagerMutation.isPending}
            onCreateOnePager={handleCreateOnePager}
            onViewOnePager={() => setOnePagerModalOpen(true)}
          />
          {onePagerModalOpen && (
            <CreateIdeaFromChatModal
              opened={onePagerModalOpen}
              onClose={() => setOnePagerModalOpen(false)}
              selectedOption={{
                label: queryText ?? onePager?.queryText ?? '',
                payload: queryText ?? onePager?.queryText ?? '',
                messageId: persistedMessageId ?? undefined,
              }}
              fetchContent={messageLevelFetchContent}
              ideaId=""
              createdBy="agent"
              sessionId={sessionId}
              messageId={persistedMessageId}
              persistOnePager={false}
              onCreated={() => onIdeaCreated?.()}
            />
          )}
          {/* Artifact-level one-pager modal — opens immediately on click; fetchContent calls /chat then saves */}
          {Boolean(onePagerArtifactId) && activeOnePagerArtifact && (
            <CreateIdeaFromChatModal
              opened={Boolean(onePagerArtifactId)}
              onClose={() => setOnePagerArtifactId(null)}
              selectedOption={{
                label: activeOnePagerArtifact.title,
                payload: activeOnePagerArtifact.title,
                messageId: persistedMessageId ?? undefined,
              }}
              fetchContent={artifactOnePagerFetchContent}
              ideaId=""
              createdBy="agent"
              sessionId={sessionId}
              messageId={persistedMessageId ?? undefined}
              artifactId={onePagerArtifactId ?? undefined}
              persistOnePager={false}
              onCreated={() => onIdeaCreated?.()}
            />
          )}
        </>
      ) : null}
    </Box>
  );
};

/**
 * HTML comments (<!-- ... -->) are block-level in CommonMark and break list structure.
 * Indented list lines after a comment are then parsed as indented code blocks (<pre>),
 * which shows raw <span> markup. For artifact splits, replace end markers with inline
 * spans before remark. Start markers are always removed.
 */
/**
 * remark-html wraps block-level HTML in `<p>…</p>`. A lone artifact end `<span>` becomes
 * `<p><span data-artifact-end …></span></p>`. Splitting on the span then leaves a dangling
 * `<p>` before the card and a stray `</p>` before follow-up text, which breaks layout and spacing.
 * Hoist the marker span out of its wrapper paragraph when it is the only content.
 */
function normalizeArtifactEndMarkerParagraphs(html: string): string {
  return html.replace(
    /<p(\s[^>]*)?>\s*(<span[^>]*\bdata-artifact-(?:end-id|end)="[^"]+"[^>]*>\s*<\/span>)\s*<\/p>/gi,
    '$2'
  );
}

function wrapExplorePostArtifactHtml(html: string): string {
  if (!html.trim()) return html;
  return `<div style="padding-top:${EXPLORE_POST_ARTIFACT_GAP_PX}px;box-sizing:border-box">${html}</div>`;
}

function prepareAssistantMarkdownForArtifacts(
  markdown: string,
  replaceEndMarkersWithSplitPoints: boolean
): string {
  let md = markdown.replace(/<!--artifact:([^:]+):start-->/g, '');
  if (replaceEndMarkersWithSplitPoints) {
    md = md.replace(
      /<!--artifact-id:([^:]+):end-->/g,
      '<span data-artifact-end-id="$1"></span>'
    );
    md = md.replace(
      /<!--artifact:([^:]+):end-->/g,
      '<span data-artifact-end="$1"></span>'
    );
  } else {
    md = md.replace(/<!--artifact-id:([^:]+):end-->/g, '');
    md = md.replace(/<!--artifact:([^:]+):end-->/g, '');
  }
  return md;
}

const ARTIFACT_DRILL_SLOT_CLASS = 'explore-artifact-drill-slot';

// Strips only a LEADING run of non-letters — list markers like "5. "/"5) " plus any
// leading emoji — not digits or emoji anywhere in the string. Stripping digits globally
// collapsed distinct titles that differ only by a number ("Option 1" vs "Option 2",
// "Q1 2024" vs "Q2 2024") to the same match key, binding the drill slot to whichever
// heading happened to match first.
const LEADING_NON_LETTER_RE = /^[^\p{L}]+/u;
// The evidence-count suffix ("— N evidence records (X%)") is always rendered as a
// `.evidence-link` span by processEvidenceLinks (its template starts with this exact
// literal) — locate that span's start instead of guessing at a dash/colon separator, since
// a title may legitimately contain an em/en dash of its own. Mirrors the boundary
// extractContextFromParent uses (`.closest('.evidence-link')`) for the same suffix.
const EVIDENCE_LINK_SPAN_MARKER = '<span class="evidence-link"';
// Separator + whitespace left dangling between the title and that span, e.g. " — ", ": ",
// " - " — same separator set extractContextFromParent trims for headings.
const TRAILING_SEPARATOR_RE = /[\s\-–—:]+$/;

function splitTitleHtmlFromEvidenceLinkSuffix(htmlFragment: string): string {
  const spanIdx = htmlFragment.indexOf(EVIDENCE_LINK_SPAN_MARKER);
  return spanIdx === -1 ? htmlFragment : htmlFragment.slice(0, spanIdx);
}

function normalizeStrongInnerForMatch(htmlFragment: string): string {
  return decodeHtmlEntities(stripHtml(splitTitleHtmlFromEvidenceLinkSuffix(htmlFragment)))
    .replace(/\s+/g, ' ')
    .trim()
    .replace(TRAILING_SEPARATOR_RE, '')
    .trim()
    .replace(LEADING_NON_LETTER_RE, '')
    .trim()
    .toLowerCase();
}

/** Insert portal host right after the title text inside the `<strong>`/`<b>`/heading
 *  (segment above the card) whose text matches the artifact title — the model sometimes
 *  renders a topic's title as a heading (`#`-`######`) instead of bold text, so both are
 *  checked. When the tag's content also carries a trailing "— N evidence records (%)"
 *  (rendered as a `.evidence-link` span), the icon goes before that span, not at the
 *  tag's end. */
function findOffsetAfterStrongMatchingArtifactTitle(html: string, artifactTitle: string): number {
  const target = normalizeStrongInnerForMatch(artifactTitle);
  if (!target) return -1;
  const re = /<(strong|b|h[1-6])(?:\s[^>]*)?>([\s\S]*?)<\/\1>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const inner = m[2];
    if (normalizeStrongInnerForMatch(inner) === target) {
      const innerStart = m.index + m[0].indexOf(inner);
      const titleOnlyHtml = splitTitleHtmlFromEvidenceLinkSuffix(inner);
      const trimmedTitleHtml = titleOnlyHtml.replace(TRAILING_SEPARATOR_RE, '');
      return innerStart + trimmedTitleHtml.length;
    }
  }
  return -1;
}

function injectExploreArtifactDrillSlot(html: string, artifactTitle: string): string {
  const at = findOffsetAfterStrongMatchingArtifactTitle(html, artifactTitle);
  if (at === -1) return html;
  const slot = `<span class="${ARTIFACT_DRILL_SLOT_CLASS}" style="display:inline-flex;vertical-align:middle;margin-left:6px"></span>`;
  return html.slice(0, at) + slot + html.slice(at);
}

function HtmlSegmentWithArtifactDrill({
  html,
  artifactTitle,
  onDrillDown,
}: {
  html: string;
  artifactTitle: string;
  onDrillDown: (topicTitle: string) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);

  const processedHtml = useMemo(
    () => injectExploreArtifactDrillSlot(html, artifactTitle),
    [html, artifactTitle]
  );

  useLayoutEffect(() => {
    const slot = wrapRef.current?.querySelector(`.${ARTIFACT_DRILL_SLOT_CLASS}`);
    setSlotEl(slot as HTMLElement | null);
    return () => setSlotEl(null);
  }, [processedHtml]);

  return (
    <>
      <Box
        ref={wrapRef}
        component="div"
        dangerouslySetInnerHTML={{__html: processedHtml}}
        sx={{
          '& p:last-child': { marginBottom: 0 },
        }}
      />
      {slotEl
        ? createPortal(
          <Tooltip label="Drill down into this topic" withArrow>
            <ActionIcon
              type="button"
              variant="subtle"
              size="sm"
              radius="xl"
              aria-label="Drill down into this topic"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDrillDown(artifactTitle);
              }}
              sx={{flexShrink: 0, color: '#4B4BE8'}}
            >
              <IconCircleChevronDown size={20} stroke={1.5}/>
            </ActionIcon>
          </Tooltip>,
          slotEl
        )
        : null}
    </>
  );
}
  
const EVIDENCE_LINK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round" style="display: inline;"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><path d="M11 7h-5a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-5"></path><line x1="10" y1="14" x2="20" y2="4"></line><polyline points="15 4 20 4 20 9"></polyline></svg>`;

function processEvidenceLinks(htmlContent: string): string {
  return htmlContent.replace(
    /<span([^>]*)\sdata-evidence-ids="([^"]+)"([^>]*)>(?:<a[^>]*>)?([^<]+)(?:<\/a>)?<\/span>/g,
    (_match, _beforeAttrs, ids, _afterAttrs, text) => {
      return `<span class="evidence-link" data-evidence-ids="${ids}" title="Click to view evidences" style="color: #5C5CEB; cursor: pointer; font-weight: 500; padding: 2px 6px; border-radius: 4px; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 4px; background-color: transparent;">${text}${EVIDENCE_LINK_ICON}</span>`;
    }
  );
}

export default ExploreChatMessageList;
